/**
 * Modal creates popup which on mobile layout fills the entire visible page.
 *
 * Example:
 * <Parent>
 *   <Modal id="UniqueIdForThisModal" isOpen={this.state.modalIsOpen} onClose={handleClose}>
 *     <FormX />
 *   </Modal>
 * </Parent>
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { Button, IconClose, NamedLink, Tooltip } from '../../components';
import { CiCircleQuestion } from 'react-icons/ci';
import { BiLinkExternal } from 'react-icons/bi';

import css from './PortfolioWorkModal.css';

const KEY_CODE_ESCAPE = 27;

const Portal = ({ children, portalRoot }) => {
  const el = useRef(document.createElement('div'));

  useEffect(() => {
    const currentEl = el.current;
    portalRoot.appendChild(currentEl);
    return () => {
      portalRoot.removeChild(currentEl);
    };
  }, [portalRoot]);

  return ReactDOM.createPortal(children, el.current);
};

const PortfolioWorkModal = ({
  children,
  className,
  scrollLayerClassName,
  closeButtonMessage,
  containerClassName,
  contentClassName,
  lightCloseButton,
  closeButtonClassName,
  intl,
  isClosedClassName = css.isClosed,
  isOpen = false,
  usePortal = false,
  moreBasis = false,
  originRect = null,
  privateWork = false,
  workId = null,
  // Target and label of the "open" link; defaults to the portfolio work page
  pageLinkName = 'PortfolioWorkPage',
  pageLinkParams = null,
  pageLinkMessage = null,
  id,
  onClose,
  onManageDisableScrolling,
}) => {
  const [portalRoot, setPortalRoot] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const refDiv = useRef(null);
  const scrollLayerRef = useRef(null);

  const handleClose = useCallback(
    event => {
      onManageDisableScrolling(id, false);
      onClose(event);
    },
    [id, onClose, onManageDisableScrolling]
  );

  const handleOverlayClick = useCallback(
    event => {
      // Only close if clicking directly on the scrollLayer (overlay), not its children
      if (event.target === event.currentTarget) {
        handleClose(event);
      }
    },
    [handleClose]
  );

  // Mount effect - set up portal root
  useEffect(() => {
    setPortalRoot(document.getElementById('portal-root'));
  }, []);

  // Manage scrolling when modal opens/closes
  useEffect(() => {
    onManageDisableScrolling(id, isOpen);
    return () => {
      onManageDisableScrolling(id, false);
    };
  }, [id, isOpen, onManageDisableScrolling]);

  // Handle animation state
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    } else {
      // Keep animating class for close animation
      const timer = setTimeout(() => {
        setIsAnimating(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Handle ESC key
  useEffect(() => {
    const handleBodyKeyUp = event => {
      if (event.keyCode === KEY_CODE_ESCAPE && isOpen) {
        handleClose(event);
      }
    };

    document.body.addEventListener('keyup', handleBodyKeyUp);
    return () => {
      document.body.removeEventListener('keyup', handleBodyKeyUp);
    };
  }, [isOpen, handleClose]);

  useEffect(() => {
    if (usePortal && isOpen && refDiv.current) {
      refDiv.current.focus();
    }
  }, [usePortal, isOpen]);

  // Reset scroll position when modal opens
  useEffect(() => {
    if (isOpen && scrollLayerRef.current) {
      scrollLayerRef.current.scrollTop = 0;
    }
  }, [isOpen]);

  const closeModalMessage = intl.formatMessage({ id: 'Modal.closeModal' });
  const closeButtonClasses =
    closeButtonClassName ||
    classNames(css.close, {
      [css.closeLight]: lightCloseButton,
    });

  const ctaBtn = isOpen ? (
    !privateWork ? (
      <NamedLink
        name={pageLinkName}
        params={pageLinkParams || { id: workId }}
        target="_blank"
        rel="noopener noreferrer"
        className={css.ctaButton}
      >
        {pageLinkMessage || <FormattedMessage id="PortfolioWorkModal.openToNewTab" />}
        <BiLinkExternal className={css.ctaIcon} />
      </NamedLink>
    ) : (
      <Tooltip
        messageId="PortfolioWorkModal.restrictedAccessTooltip"
        rootClassName={css.ctaButton}
        direction="bottom"
      >
        <div className={css.ctaIconWrapper}>
          <FormattedMessage id="PortfolioWorkModal.restrictedAccess" />
          <CiCircleQuestion className={css.ctaIcon} />
        </div>
      </Tooltip>
    )
  ) : null;

  const closeBtn = isOpen ? (
    <Button onClick={handleClose} rootClassName={closeButtonClasses} title={closeModalMessage}>
      <span className={css.closeText}>
        {closeButtonMessage || <FormattedMessage id="Modal.close" />}
      </span>
      <IconClose rootClassName={css.closeIcon} />
    </Button>
  ) : null;

  const buttonGroup = isOpen ? (
    <div className={css.buttonGroup}>
      {ctaBtn}
      {closeBtn}
    </div>
  ) : null;

  const modalClass = isOpen ? css.isOpen : isClosedClassName;
  const classes = classNames(modalClass, className, {
    [css.hasOrigin]: originRect && (isOpen || isAnimating),
  });
  const scrollLayerClasses = scrollLayerClassName || css.scrollLayer;
  const containerClasses = containerClassName || css.container;

  const basisMaybe = moreBasis ? { style: { flexBasis: '1200px' } } : {};

  // Calculate initial transform for animation
  const getInitialTransform = () => {
    if (!originRect || !isOpen) return {};

    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    // Calculate the center of the clicked image
    const originCenterX = originRect.left + originRect.width / 2;
    const originCenterY = originRect.top + originRect.height / 2;

    // Calculate the center of the viewport
    const viewportCenterX = windowWidth / 2;
    const viewportCenterY = windowHeight / 2;

    // Calculate translation needed
    const translateX = originCenterX - viewportCenterX;
    const translateY = originCenterY - viewportCenterY;

    // Calculate scale based on image size vs viewport
    const scaleX = originRect.width / windowWidth;
    const scaleY = originRect.height / windowHeight;
    const scale = Math.min(scaleX, scaleY, 0.3);

    return {
      '--origin-x': `${translateX}px`,
      '--origin-y': `${translateY}px`,
      '--origin-scale': scale,
    };
  };

  const animationStyle = originRect ? getInitialTransform() : {};

  return !usePortal ? (
    <div className={classes} style={animationStyle}>
      <div className={scrollLayerClasses} onClick={handleOverlayClick} ref={scrollLayerRef}>
        <div className={containerClasses}>
          {buttonGroup}
          <div className={classNames(contentClassName || css.content)}>{children}</div>
        </div>
      </div>
    </div>
  ) : portalRoot ? (
    <Portal portalRoot={portalRoot}>
      <div className={classes} style={animationStyle}>
        <div className={scrollLayerClasses} onClick={handleOverlayClick} ref={scrollLayerRef}>
          <div
            className={classNames(containerClasses, css.focusedDiv)}
            {...basisMaybe}
            ref={refDiv}
            tabIndex="-1"
          >
            {buttonGroup}
            <div className={classNames(contentClassName || css.content)}>{children}</div>
          </div>
        </div>
      </div>
    </Portal>
  ) : null;
};

PortfolioWorkModal.defaultProps = {
  children: null,
  className: null,
  scrollLayerClassName: null,
  closeButtonMessage: null,
  containerClassName: null,
  contentClassName: null,
  lightCloseButton: false,
  isClosedClassName: css.isClosed,
  isOpen: false,
  usePortal: false,
  originRect: null,
  privateWork: null,
  workId: null,
  pageLinkName: 'PortfolioWorkPage',
  pageLinkParams: null,
  pageLinkMessage: null,
};

const { bool, func, node, string } = PropTypes;

PortfolioWorkModal.propTypes = {
  children: node,
  className: string,
  scrollLayerClassName: string,
  closeButtonMessage: node,
  containerClassName: string,
  contentClassName: string,
  lightCloseButton: bool,
  id: string.isRequired,
  intl: intlShape.isRequired,
  isClosedClassName: string,
  isOpen: bool,
  onClose: func.isRequired,
  usePortal: bool,
  originRect: PropTypes.shape({
    top: PropTypes.number,
    left: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  privateWork: bool,
  workId: string,
  pageLinkName: string,
  pageLinkParams: PropTypes.object,
  pageLinkMessage: node,
  onManageDisableScrolling: func.isRequired,
};

export default injectIntl(PortfolioWorkModal);
